'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { IFooter } from '@/sanity/lib/definitions';

import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import Logo from '@/components/global/Logo';
import { resolvePageLink } from '@/sanity/globals/CTA';
import { CTAButton } from '@/shadcn/ui/button';
import { useCookieBanner } from 'cookiebanner-finally';
import Image from 'next/image';
import bgLogo from '@/public/footer/bg-logo.svg';
import bgLogoMobile from '@/public/footer/bg-logoMobile.svg';
import { useIsMobile } from '@/hooks/useIsMobile';

export default function Footer({
  data,
  className,
}: {
  data: IFooter;
  className?: string;
}) {
  const { show } = useCookieBanner();
  const isMobile = useIsMobile();
  
  return (
    <footer
      className={cn(
        'pt-[8rem] lg:pt-[13.13rem] pb-[26rem] lg:pb-[48.41rem] bg-fakeblack',
        className,
      )}
    >
      <div className="container flex flex-col gap-[7rem] lg:gap-[17.28rem] max-w-[208rem]">
        <div className="flex items-start flex-wrap lg:gap-0 gap-[5rem] justify-between">
          <div className="flex flex-col items-start gap-[2rem] max-w-[64.75rem]">
            <Logo />

            <div className="max-w-[35rem] lg:max-w-[29.25rem] text-[#F6F4E9]/30 prose-p:text-[1.75rem] prose-p:font-[400] prose-p:leading-[120%]">
              <PortableText value={data.about} components={richTextComponent} />
            </div>
          </div>
          <div className="grid grid-cols-2 lg:flex gap-[4rem] lg:gap-[13rem] w-full lg:w-fit">
            <div className="flex flex-col items-start gap-[2rem] w-full lg:w-fit">
              <h3 className="uppercase font-[400] leading-[120%] text-[#F6F4E9]/40 text-[1.75rem]">
                SERVICES
              </h3>
              <ul className="flex flex-col gap-[.75rem]">
                {data.services?.map((item, index) => (
                  <li key={index}>
                    <Link
                      style={{
                        textWrap: 'nowrap',
                      }}
                      className="text-[#F6F4E9]/60 leading-none text-[1.75rem] font-[400] transition-all duration-300 hover:opacity-100 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[0.0625rem] after:bg-offwhite after:transition-all after:duration-300 hover:after:w-full"
                      href={resolvePageLink(item.link) || '/'}
                      target={item.link?.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.link?.openInNewTab
                          ? 'noopener noreferrer'
                          : undefined
                      }
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-start gap-[2rem] w-full lg:w-fit">
              <h3 className="uppercase font-[400] leading-[120%] text-[#F6F4E9]/40 text-[1.75rem]">
                RESOURCES
              </h3>
              <ul className="flex flex-col gap-[.75rem]">
                {data.resources?.map((item, index) => (
                  <li key={index}>
                    <Link
                      className="text-[#F6F4E9]/60 leading-none text-[1.75rem] font-[400] transition-all duration-300 hover:opacity-100 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[0.0625rem] after:bg-offwhite after:transition-all after:duration-300 hover:after:w-full"
                      href={resolvePageLink(item.link) || '/'}
                      target={item.link?.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.link?.openInNewTab
                          ? 'noopener noreferrer'
                          : undefined
                      }
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="flex flex-col items-start gap-[2rem] w-full lg:w-fit">
              <h3 className="uppercase font-[400] leading-[120%] text-[#F6F4E9]/40 text-[1.75rem]">
                CONTACT
              </h3>
              <ul className="flex flex-col gap-[.75rem]">
                {data.contact?.map((item, index) => (
                  <li key={index}>
                    <Link
                      className="text-[#F6F4E9]/60 leading-none text-[1.75rem] font-[400] transition-all duration-300 hover:opacity-100 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[0.0625rem] after:bg-offwhite after:transition-all after:duration-300 hover:after:w-full"
                      href={resolvePageLink(item.link) || '/'}
                      target={item.link?.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.link?.openInNewTab
                          ? 'noopener noreferrer'
                          : undefined
                      }
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="flex flex-col items-start gap-[2rem] w-full lg:w-fit">
              <h3 className="uppercase font-[400] leading-[120%] text-[#F6F4E9]/40 text-[1.75rem]">
                LEGAL
              </h3>
              <ul className="flex flex-col gap-[.75rem]">
                {data.legal?.map((item, index) => (
                  <li key={index}>
                    <Link
                      className="text-[#F6F4E9]/60 leading-none text-[1.75rem] font-[400] transition-all duration-300 hover:opacity-100 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[0.0625rem] after:bg-offwhite after:transition-all after:duration-300 hover:after:w-full"
                      href={resolvePageLink(item.link) || '/'}
                      target={item.link?.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.link?.openInNewTab
                          ? 'noopener noreferrer'
                          : undefined
                      }
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-end flex-wrap-reverse lg:gap-0 gap-[3.88rem] justify-between">
          <p className="text-[#F6F4E9]/25 text-[1.5rem] font-[400] mb-0 leading-[120%]">
            © {new Date().getFullYear()} RevUp. All rights reserved. Engineered growth for B2B companies.
          </p>
          <Link className="text-[#F6F4E9] text-[4rem] font-[400] leading-[120%] transition-all duration-300 ease-in-out hover:text-purple" href="mailto:info@veylor.com">
            hello@revup.io
          </Link>
        </div>
      </div>

      <Image
        src={isMobile ? bgLogoMobile : bgLogo}
        alt="RevUp"
        className="w-full lg:w-[208rem] h-auto absolute bottom-0 left-1/2 -translate-x-1/2"
        quality={100}
      />
    </footer>
  );
}
